import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';

import { queryKeysDataExchange } from './queryKeys';
import {
  type DataExchangeContactAttempt,
  type DataExchangeContactChannel,
} from './useQueryDataExchange';

type DataExchangeContactAttemptPayload = {
  channel: DataExchangeContactChannel;
  dataExchangeId: UUID;
  id: UUID;
  notes: string;
  timestamp: string;
};

/**
 * Creates a contact attempt for a specified data exchange.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Exchange/operation/add_contact_attempts_data_exchange_contact_attempt_put

 */
export const createContactAttempt = async (
  contactAttempt: DataExchangeContactAttemptPayload,
) => {
  const body = withoutObjectKeysWhereValueIs(contactAttempt, [undefined, null]);

  const response = await vestigasApi
    .put(ENDPOINT.DATA_EXCHANGE.CREATE_CONTACT_ATTEMPT(), {
      json: body,
    })
    .json<DataExchangeContactAttempt>();

  return response;
};

/**
 * Custom hook for creating a contact attempt for a given data exchange using mutations.
 *
 * @returns {Object} An object containing the mutation function and other properties.
 */
export const useMutationCreateContactAttempt = (
  options?: UseMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contactAttempt: DataExchangeContactAttemptPayload) =>
      createContactAttempt(contactAttempt),
    onSuccess(_, { dataExchangeId }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysDataExchange.getAllSuppliers(undefined),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysDataExchange.get(dataExchangeId),
      });
    },
    ...options,
  });
};
